import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Box, Container, ThemeProvider, Typography,
} from '@material-ui/core';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import {
  Product, getPdtUid, getProductMinAge,
} from '@/utils/productHelpers';
import {
  validateZipCode, validateEmployeeId, getAge,
} from '@/utils/stringValidators';
import { parseDate, formatDateString } from '@/utils/stringUtils';
import { formatDateInput } from '@/utils/inputHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY, ME_QUERY } from '@/apollo/queries';
import { EDIT_PATIENT_INFO, CONSUMER_APP_SAVE_COVERAGE_DETAILS, ACTIVATE_PATIENT } from '@/apollo/mutations';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';

const CoverageCheckInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { product, org_slug: orgSlug } = useParams<{ product: Product; org_slug: string }>();
  const { state: locationState } = useLocation<{
    patientUid?: string; phoneNumber?: string; flow?: string;
    accessToken?: string;
  }>();
  const showPhoneField = locationState?.flow === 'email';
  const { t } = useTranslation();

  // Fetch org eligibility config
  const { data: configData } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: { organizationSlug: orgSlug, pdtUid: getPdtUid(product) },
    skip: !orgSlug,
    context: locationState?.accessToken
      ? { headers: { Authorization: `Bearer ${locationState.accessToken}` } }
      : undefined,
  });
  const config = configData?.eligibilityConfiguration;
  const showZipCode = config?.collectZipcodeForEligibility ?? false;
  const showEmployeeId = config?.hasEmployeeId ?? false;
  const employeeIdLabel = config?.employeeIdFieldRename || t('coverage.spark.patient.employee_id_label');
  const minAge = getProductMinAge(product);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(locationState?.phoneNumber || '');

  // Error state
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [dobError, setDobError] = useState('');
  const [zipCodeError, setZipCodeError] = useState(false);
  const [employeeIdError, setEmployeeIdError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [isIneligible, setIsIneligible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editPatientInfo] = useMutation(EDIT_PATIENT_INFO);
  const [saveCoverageDetails] = useMutation(CONSUMER_APP_SAVE_COVERAGE_DETAILS);
  const [activatePatient] = useMutation(ACTIVATE_PATIENT);
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });

  // Validation — dob stored as "MM / DD / YYYY" (14 chars), same as UserInfoConfirmation
  const isDobValid = dob.length === 14 && (() => {
    const parsed = parseDate(dob);
    const date = new Date(parsed);
    const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900;
    return isValidDate && getAge(dob) >= minAge;
  })();
  const isFormValid = firstName.trim() !== ''
    && lastName.trim() !== ''
    && isDobValid
    && (showZipCode ? validateZipCode(zipCode) : true)
    && (showEmployeeId ? employeeId.trim() !== '' : true);

  const handleSubmit = async () => {
    setGeneralError('');

    try {
      setIsSubmitting(true);

      // Get tokens — from location state (email/phone flow) or getSession fallback
      let accessToken = locationState?.accessToken;
      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!accessToken) {
        accessToken = session?.access_token ?? undefined;
      }
      const refreshToken = session?.refresh_token ?? undefined;

      if (!accessToken) {
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      // Single mutation: checks eligibility + saves coverage + updates patient info
      const coverageResponse = await saveCoverageDetails({
        variables: {
          pdtUid: getPdtUid(product),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: formatDateString(dob),
          coverageCheckTimestamp: new Date().toISOString(),
          coverageCheckStatus: 'pass',
          postalCode: zipCode || undefined,
          employeeId: showEmployeeId ? employeeId.trim() : undefined,
        },
        context: {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      });

      if (!coverageResponse.data?.consumerAppSaveCoverageDetails?.success) {
        setIsIneligible(true);
        setIsSubmitting(false);
        return;
      }

      // Activate patient care team (employer flow — no access code)
      const activateResponse = await activatePatient({
        variables: {
          pdtUid: getPdtUid(product),
          organizationSlug: orgSlug,
        },
        context: {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      });

      if (!activateResponse.data?.activatePatient?.success) {
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      // Save phone if provided (saveCoverageDetails doesn't handle phone)
      if (phoneNumber) {
        await editPatientInfo({
          variables: {
            changeReason: 'updating phone from spark employer activation flow',
            phone: phoneNumber,
          },
          context: {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        });
      }

      // Session handoff
      if (!refreshToken) {
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

      // Browser context: no in-app deep link available — route to download page.
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        window.location.href = `/appstore/${product}`;
        return;
      }

      const { exchangeToken } = await requestAuthCode(accessToken, refreshToken);

      const { data: meData } = await fetchMe({
        context: { headers: { Authorization: `Bearer ${accessToken}` } },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    } catch (err) {
      console.error('[ActivationEligibilityCheck] submit failed:', err);
      setGeneralError(t('coverage.errors.generic_error'));
    }
  };

  if (isIneligible) {
    return (
      <ThemeProvider theme={sparkTheme}>
        <Box display="flex" flexDirection="column" minHeight="100vh">
          <Header />
          <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
            <Typography variant="h3" style={{ marginBottom: 8 }}>
              {t('coverage.spark.patient.coverage_rejection.title')}
            </Typography>
            <Typography style={{ marginBottom: 8 }}>
              {t('coverage.spark.patient.coverage_rejection.not_eligible')}
            </Typography>
            <Typography>
              {t('coverage.spark.patient.coverage_rejection.contact')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              rounded
              style={{ borderRadius: 100, marginTop: 16 }}
              onClick={() => setIsIneligible(false)}
            >
              {t('coverage.spark.patient.retry_spark_registration')}
            </Button>
          </Container>
          <Footer />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 16 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.spark.patient.coverage_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.spark.patient.coverage_subtitle')}
          </Typography>
        </Box>

        <Box style={{ marginBottom: 32 }}>
          <TextField
            required
            error={firstNameError}
            helperText={firstNameError ? t('coverage.errors.first_name_error') : ''}
            label={t('coverage.spark.patient.f_name_label')}
            inputProps={{ placeholder: t('coverage.spark.patient.f_name_label'), 'data-testid': 'first-name-input' }}
            value={firstName}
            onChange={(e) => { setFirstNameError(false); setFirstName(e.target.value); }}
            onBlur={(e) => setFirstNameError(e.target.value.trim() === '')}
          />
          <TextField
            required
            error={lastNameError}
            helperText={lastNameError ? t('coverage.errors.last_name_error') : ''}
            label={t('coverage.spark.patient.l_name_label')}
            inputProps={{ placeholder: t('coverage.spark.patient.l_name_label'), 'data-testid': 'last-name-input' }}
            value={lastName}
            onChange={(e) => { setLastNameError(false); setLastName(e.target.value); }}
            onBlur={(e) => setLastNameError(e.target.value.trim() === '')}
          />
          {showZipCode && (
            <TextField
              required
              error={zipCodeError}
              helperText={zipCodeError ? t('coverage.errors.zipcode_error') : ''}
              label={t('coverage.spark.patient.zipcode_label')}
              inputProps={{ placeholder: t('coverage.spark.patient.zipcode_label'), 'data-testid': 'zipcode-input' }}
              value={zipCode}
              onChange={(e) => { setZipCodeError(false); setZipCode(e.target.value); }}
              onBlur={(e) => setZipCodeError(!validateZipCode(e.target.value))}
            />
          )}
          {showEmployeeId && (
            <TextField
              required
              error={!!employeeIdError}
              helperText={employeeIdError}
              label={employeeIdLabel}
              inputProps={{
                placeholder: config?.employeeIdExample || employeeIdLabel,
                'data-testid': 'employee-id-input',
              }}
              value={employeeId}
              onChange={(e) => { setEmployeeIdError(''); setEmployeeId(e.target.value); }}
              onBlur={(e) => {
                const result = validateEmployeeId(e.target.value.trim(), {
                  validationEnabled: config?.employeeIdValidationEnabled,
                  allowedChars: config?.employeeIdAllowedChars,
                  minLength: config?.employeeIdMinLength,
                  maxLength: config?.employeeIdMaxLength,
                  errorMessage: config?.employeeIdValidationErrorMessage,
                });
                if (!result.isValid) {
                  setEmployeeIdError(result.errorMessage || t('coverage.errors.employee_id_error'));
                }
              }}
            />
          )}
          <TextField
            required
            error={!!dobError}
            helperText={dobError}
            label={t('coverage.spark.patient.dob_label')}
            inputProps={{
              placeholder: 'MM / DD / YYYY',
              maxLength: 14,
              inputMode: 'numeric' as const,
              'data-testid': 'dob-input',
            }}
            value={formatDateInput(dob)}
            onChange={(e) => {
              setDobError('');
              const value = formatDateInput(e.target.value);
              setDob(value);
            }}
            onBlur={() => {
              if (dob.length === 14) {
                const parsed = parseDate(dob);
                const date = new Date(parsed);
                const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= 1900;
                if (isValidDate && getAge(dob) < minAge) {
                  setDobError(t('coverage.spark.patient.dob_age_error'));
                } else if (!isValidDate) {
                  setDobError(t('coverage.errors.dob_error'));
                }
              } else if (dob) {
                setDobError(t('coverage.errors.dob_error'));
              }
            }}
          />

          {showPhoneField && (
            <PhoneNumberInput
              id="phoneNumber"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value || '')}
              onBlur={() => {}}
              placeholder={t('coverage.spark.patient.phone_label')}
              error={false}
              helperText=""
              required={false}
              data-testid="phone-input"
            />
          )}

          {generalError && (
            <Typography color="error" style={{ marginTop: 8 }} data-testid="general-error">
              {generalError}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          type="button"
          data-testid="check-coverage-button"
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
          loading={isSubmitting}
          style={{ borderRadius: 100 }}
        >
          {t('coverage.check_coverage')}
        </Button>

        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

const ActivationEligibilityCheck: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <CoverageCheckInner />
  </ThemeProvider>
);

export default ActivationEligibilityCheck;
